import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import type { APInvoiceSnoozePreset } from '@erp/shared-types';
import {
  formatSnoozedUntil,
  fromDateTimeLocalValue,
  snoozePresetUntil,
  toDateTimeLocalValue,
  type SnoozeWarnings,
} from './invoiceSnooze';

const PRESETS = ['tomorrow', 'three_days', 'one_week'] as const;

export function SnoozeDialogDescription({ count }: { count: number }) {
  if (count === 1) {
    return (
      <T context="explanation of snoozing one AP invoice">
        Hide this invoice from the invoices list until the time you pick.
        Opening it directly still works. Status, due date, and tasks stay as
        they are.
      </T>
    );
  }
  return (
    <T context="explanation of snoozing several AP invoices">
      Hide these invoices from the invoices list until the time you pick. Only
      drafts can be snoozed.
    </T>
  );
}

function presetLabel(
  gt: ReturnType<typeof useGT>,
  preset: (typeof PRESETS)[number],
): string {
  if (preset === 'tomorrow') {
    return gt('Tomorrow', {
      context: 'invoice snooze preset, 9:00 tomorrow morning',
    });
  }
  if (preset === 'three_days') {
    return gt('3 Days', {
      context: 'invoice snooze preset, 9:00 three days from now',
    });
  }
  return gt('1 Week', {
    context: 'invoice snooze preset, 9:00 one week from now',
  });
}

export function SnoozePresetPicker({
  preset,
  specific,
  onPreset,
  onSpecific,
}: {
  preset: APInvoiceSnoozePreset;
  specific: string;
  onPreset: (preset: APInvoiceSnoozePreset) => void;
  onSpecific: (value: string) => void;
}) {
  const gt = useGT();
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((item) => (
          <Button
            key={item}
            type="button"
            size="sm"
            variant={preset === item ? 'default' : 'outline'}
            className="h-auto flex-col gap-0 py-1"
            onClick={() => onPreset(item)}
          >
            <span>{presetLabel(gt, item)}</span>
            <span className="text-xs font-normal leading-tight opacity-80">
              {formatSnoozedUntil(snoozePresetUntil(item))}
            </span>
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant={preset === 'specific' ? 'default' : 'outline'}
          onClick={() => onPreset('specific')}
        >
          <T context="invoice snooze preset that opens a date and time picker">
            Pick a time
          </T>
        </Button>
      </div>
      {preset === 'specific' && (
        <SnoozeSpecificUntil value={specific} onChange={onSpecific} />
      )}
    </>
  );
}

function timeInputValue(date: Date | null): string {
  if (!date) return '09:00';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function withTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':');
  const next = new Date(date);
  next.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
  return next;
}

/** The app calendar for the day, plus a time field. Presets stay at 9:00. */
function SnoozeSpecificUntil({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const gt = useGT();
  const selected = fromDateTimeLocalValue(value);
  const time = timeInputValue(selected);

  function commit(date: Date | undefined, nextTime: string) {
    if (!date || !nextTime) {
      onChange('');
      return;
    }
    onChange(toDateTimeLocalValue(withTime(date, nextTime)));
  }

  return (
    <div className="flex items-center gap-2">
      <DatePicker
        value={selected ?? undefined}
        onChange={(date) => commit(date, time)}
      />
      <Input
        type="time"
        value={time}
        aria-label={gt('Snooze until', {
          context: 'label for the invoice snooze date and time input',
        })}
        onChange={(event) => commit(selected ?? undefined, event.target.value)}
        className="h-8 w-[7.5rem]"
      />
    </div>
  );
}

export function SnoozeWarningCopy({
  warnings,
  count,
}: {
  warnings: SnoozeWarnings;
  count: number;
}) {
  return (
    <div className="space-y-1 rounded border border-amber-500 bg-amber-50 p-2 text-sm text-amber-900">
      {warnings.tasks && <SnoozeTaskWarning count={count} />}
      {warnings.pastDue && (
        <p>
          <T context="warning that an invoice snooze lasts past the due date">
            This snooze lasts past the due date. The due date does not move.
          </T>
        </p>
      )}
    </div>
  );
}

function SnoozeTaskWarning({ count }: { count: number }) {
  if (count === 1) {
    return (
      <p>
        <T context="warning that snoozing one invoice does not hide its tasks">
          Tasks on this invoice stay visible and keep running.
        </T>
      </p>
    );
  }
  return (
    <p>
      <T context="warning that snoozing invoices does not hide their tasks">
        Tasks on these invoices stay visible and keep running.
      </T>
    </p>
  );
}

export function SnoozeSubmitLabel({ confirming }: { confirming: boolean }) {
  if (confirming) {
    return (
      <T context="confirm invoice snooze after the warning">Confirm Snooze</T>
    );
  }
  return <T context="submit the invoice snooze dialog">Snooze</T>;
}
